/**
 * A single task's screen — before the start and while it runs.
 *
 * One screen, two states, because they are the same object at different points:
 * before starting it explains and asks one question, after starting it records.
 * Splitting them would duplicate the whole header for one button's difference.
 *
 * Every "start and report live in the Tasks tab" note elsewhere in the app was
 * replaced by a link here. A sentence telling someone where to go is worse than
 * taking them there.
 */
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { formatDate } from '../i18n/detectLanguage';
import { useTt } from '../i18n/useT';
import { AppShell, DeskRow } from './AppShell';
import { useAppContent } from './content';
import { useProfile } from './profile';
import { Icon } from './icons';
import { XpTag } from './XpTag';
import { Appear, Body, Btn, Col, Fluid, Meta, Muted, Panel, Rail, Row, useIsDesktop } from './primitives';
import { c, hair, hair2, f, ACT, patternZone, LOAD, DESKTOP, DAY_KEYS } from './tokens';
import { R } from './routes';
import { useTasks, doneCount, streakOf, isDoneToday, isLoggedToday, weekOf } from './tasks';

const TaskPage: React.FC = () => {
    const { code = '' } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    const { lang } = useLang();
    const { t, tn } = useTt();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const desktop = useIsDesktop(DESKTOP);
    const { list, take, drop, setNote } = useTasks();

    const ex = content?.exercises.find((e) => e.code === code);
    const task = list.find((t) => t.code === code);
    const pat = ex?.pats[0] ? content?.patBy[ex.pats[0]] : null;
    const mine = pat ? profile.patterns.find((p) => p.code === pat.code && p.pct != null) : null;
    const zone = mine ? patternZone(mine.pct as number, mine.pol) : null;
    const accent = zone?.hex || ACT;

    // Only the pre-start answer is held here. Once the task runs there is nothing
    // on this screen left to type into.
    const [draft, setDraft] = React.useState('');

    const back = React.useCallback(() => navigate(R.tasks), [navigate]);
    const scoreOf = React.useCallback(
        (dir: string) => profile.directions.find((x) => x.code === dir)?.score ?? null,
        [profile.directions],
    );

    if (!content || !ex) {
        return (
            <AppShell active="tasks" title={t('app.tab.tasks')}>
                <Col style={{ padding: '60px 20px', alignItems: 'center' }} gap={10}>
                    <Meta style={{ color: c.subtle }}>
                        {t(content ? 'app.task.notFound' : 'app.state.loading')}
                    </Meta>
                    {content && <Btn onClick={back}>{t('app.task.backToList')}</Btn>}
                </Col>
            </AppShell>
        );
    }

    const started = !!task;
    const days = task?.days ?? 7;
    const week = task ? weekOf(task) : [];
    const streak = task ? streakOf(task) : 0;
    const marks = task ? doneCount(task) : 0;
    // Newest first, capped — the archive is a reminder, not a ledger.
    const allEntries = task ? Object.entries(task.log).filter(([, e]) => e.done) : [];
    const history = allEntries.sort((a, b) => (a[0] < b[0] ? 1 : -1)).slice(0, 10);

    // Starting and reporting are one motion: take the task, keep the answer as
    // the note, and land straight on the day — the user pressed a verb, not a
    // toggle.
    const onStart = () => {
        // One call: the note travels with the take, so the server row is never
        // created noteless and patched a beat later.
        take(ex.code, { note: draft.trim() });
        navigate(R.taskDay(ex.code));
    };

    // The first sentence of the description doubles as the screen's lead line.
    // Split on the sentence end, not on a stored second field — one text, two
    // sizes, so there is nothing to keep in sync.
    const cut = ex.desc.indexOf('. ');
    const lead = cut > 0 ? ex.desc.slice(0, cut + 1) : ex.desc;
    const rest = cut > 0 ? ex.desc.slice(cut + 2) : '';

    // Four universal report fields today; a per-exercise schema would replace the
    // constant here and nothing else on this screen.
    const reportFields = 4;

    const end = new Date();

    end.setDate(end.getDate() + days);
    const endLabel = formatDate(lang, end, { day: 'numeric', month: 'long' });

    /* ── blocks ──────────────────────────────────────── */

    // The exercise name is already the screen title, so the card opens with what
    // the task is worth and which pattern it is for — not with the name again.
    const header = (
        <div>
            <Row style={{ gap: 9, flexWrap: 'wrap', alignItems: 'center' }}>
                <XpTag ex={ex} content={content} scoreOf={scoreOf} patterns />
                {pat && (
                    <>
                        <Meta style={{ fontSize: 10, color: c.subtle }}>·</Meta>
                        <Link to={R.pattern(pat.code)} style={{ textDecoration: 'none' }}>
                            <Meta style={{ fontSize: 10 }}>{pat.name}</Meta>
                        </Link>
                    </>
                )}
            </Row>
            {/* The description's own first sentence carries the screen — it is the
                one-line version of the exercise. The rest follows as body copy. */}
            <div
                style={{
                    fontSize: desktop ? 19 : 17,
                    fontWeight: 400,
                    lineHeight: 1.35,
                    letterSpacing: '-0.015em',
                    color: c.textPri,
                    marginTop: 15,
                }}
            >
                {lead}
            </div>
            {rest && <Body style={{ marginTop: 11, fontSize: desktop ? 14.5 : 14, lineHeight: 1.55 }}>{rest}</Body>}
            {pat?.aha && (
                <div
                    style={{
                        marginTop: 17,
                        padding: '15px 17px',
                        borderRadius: 14,
                        border: `1px solid ${accent}30`,
                        background: `linear-gradient(140deg, ${accent}0f, ${c.cardIn} 60%)`,
                    }}
                >
                    <Meta style={{ fontSize: 9, letterSpacing: '0.16em', color: accent, display: 'block' }}>
                        {t('app.task.insight')}
                    </Meta>
                    <div
                        style={{
                            fontFamily: f.serif,
                            fontStyle: 'italic',
                            fontWeight: 300,
                            fontSize: desktop ? 17 : 15.5,
                            lineHeight: 1.4,
                            color: c.textPri,
                            marginTop: 9,
                        }}
                    >
                        {pat.aha}
                    </div>
                </div>
            )}
        </div>
    );

    // Before the start the row describes the task; once it runs it reports on it.
    // Same strip, because it answers the same question at two different moments.
    let metricPairs: Array<[string, string]> = [
        [t('app.task.cadence'), tn('app.task.days', days)],
        [t('app.task.perDay'), ex.min ? tn('app.task.minutes', ex.min) : t('app.task.ruleLower')],
        [tn('app.task.steps', ex.flow.length, { n: ex.flow.length }), String(ex.flow.length)],
        [t('app.task.load'), t(LOAD[ex.load] || ex.load)],
    ];

    if (started) {
        metricPairs = [
            [t('app.task.thisWeek'), `${week.filter(Boolean).length}/7`],
            [t('app.task.streak'), String(streak)],
            [t('app.task.entries'), String(marks)],
            [t('app.task.minPerDay'), ex.min ? String(ex.min) : '–'],
        ];
    }

    const metrics = (
        <Row style={{ paddingTop: 16, marginTop: 18, borderTop: hair, gap: 14, flexWrap: 'wrap' }}>
            {metricPairs.map(([l, v]) => (
                <Col key={l} style={{ flex: '1 1 80px', minWidth: 0 }} gap={4}>
                    <span
                        style={{
                            fontFamily: f.mono,
                            fontSize: 19,
                            fontWeight: 300,
                            letterSpacing: '-0.03em',
                            color: c.textPri,
                        }}
                    >
                        {v}
                    </span>
                    <Meta style={{ fontSize: 9, letterSpacing: '0.1em' }}>{l}</Meta>
                </Col>
            ))}
            {started && (
                <Row style={{ gap: 5, flex: '0 0 auto', alignItems: 'flex-start' }}>
                    {week.map((on, i) => (
                        <Col key={DAY_KEYS[i]} style={{ alignItems: 'center' }} gap={5}>
                            <div
                                style={{
                                    width: 22,
                                    height: 4,
                                    borderRadius: 2,
                                    background: on ? ACT : 'rgba(232,230,224,0.14)',
                                }}
                            />
                            <span style={{ fontFamily: f.mono, fontSize: 8.5, color: c.subtle }}>
                                {t(DAY_KEYS[i])}
                            </span>
                        </Col>
                    ))}
                </Row>
            )}
        </Row>
    );

    // Numbered because the order is the method — the steps are not a checklist
    // you can take in any sequence.
    const flow = ex.flow.length ? (
        <div>
            <Row style={{ justifyContent: 'space-between', marginBottom: 14 }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.task.flow')}</Meta>
                <Meta style={{ fontSize: 9.5, color: c.subtle }}>{tn('app.task.stepCount', ex.flow.length)}</Meta>
            </Row>
            <Col gap={13}>
                {ex.flow.map((step, i) => (
                    <Row key={step.slice(0, 24)} style={{ gap: 12, alignItems: 'flex-start' }}>
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 11,
                                color: accent,
                                flex: '0 0 auto',
                                width: 16,
                                marginTop: 2,
                            }}
                        >
                            {i + 1}
                        </span>
                        <Body style={{ fontSize: 13.5 }}>{step}</Body>
                    </Row>
                ))}
            </Col>
        </div>
    ) : null;

    // "Why this works" lives on the day screen, next to the instruction — this
    // screen is the overview, not the reading.
    const changes = pat?.res?.length ? (
        <div>
            <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.task.changes')}</Meta>
                <Meta style={{ fontSize: 9.5, color: c.subtle }}>{tn('app.dir.helps.meta', pat.res.length)}</Meta>
            </Row>
            <Col gap={11}>
                {pat.res.map((line) => (
                    <Row key={line.slice(0, 24)} style={{ gap: 12, alignItems: 'flex-start' }}>
                        <Icon k="check" size={13} color="#4ade80" style={{ marginTop: 3, flex: '0 0 auto' }} />
                        <Body style={{ fontSize: 13.5 }}>{line}</Body>
                    </Row>
                ))}
            </Col>
        </div>
    ) : null;

    const setup = ex.setupQ ? (
        <div>
            <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.task.setup')}</Meta>
                <Meta style={{ fontSize: 9.5, color: c.subtle }}>{t('app.task.setup.skip')}</Meta>
            </Row>
            <span
                style={{ fontSize: 15, color: c.textPri, fontWeight: 400, letterSpacing: '-0.015em', lineHeight: 1.35 }}
            >
                {ex.setupQ}
            </span>
            <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('app.task.setup.placeholder')}
                rows={2}
                style={{
                    width: '100%',
                    marginTop: 12,
                    resize: 'vertical',
                    background: c.card,
                    border: `1px solid ${draft ? ACT : 'rgba(232,230,224,0.08)'}`,
                    borderRadius: 14,
                    padding: '14px 15px',
                    color: c.textPri,
                    fontFamily: f.ui,
                    fontSize: 14,
                    lineHeight: 1.55,
                    outline: 'none',
                }}
            />
            <Row style={{ gap: 9, marginTop: 11, alignItems: 'flex-start' }}>
                <Icon k="info" size={13} color={c.subtle} style={{ marginTop: 1, flex: '0 0 auto' }} />
                <Muted style={{ fontSize: 11.5 }}>{t('app.task.setup.note')}</Muted>
            </Row>
        </div>
    ) : null;

    const historyBlock = task && history.length > 0 && (
        <div>
            <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em' }}>{t('app.task.history')}</Meta>
                <Meta style={{ fontSize: 9.5, color: c.subtle }}>
                    {tn('app.task.entryCount', allEntries.length)}
                </Meta>
            </Row>
            <Col gap={0}>
                {history.map(([day, entry], i) => (
                    <Row key={day} style={{ gap: 12, padding: '11px 0', borderTop: i ? hair2 : 'none' }}>
                        <Icon k="check" size={12} color="#4ade80" style={{ flex: '0 0 auto' }} />
                        <Meta style={{ fontSize: 9.5, color: c.muted, width: 78, flex: '0 0 auto' }}>
                            {formatDate(lang, new Date(day), { day: 'numeric', month: 'short' })}
                        </Meta>
                        <Muted style={{ fontSize: 12.5, flex: 1, minWidth: 0 }}>
                            {entry.text || t('app.task.entryDone')}
                        </Muted>
                    </Row>
                ))}
            </Col>
        </div>
    );

    const loggedToday = task ? isLoggedToday(task) : false;

    const dayNo = Math.min(marks + 1, days);
    // Segments, not a percentage: the cycle is counted in days, and a day is
    // either recorded or it is not.
    const segments = (
        <Row style={{ gap: 5 }}>
            {Array.from({ length: days }, (_, i) => {
                let seg = 'rgba(232,230,224,0.14)';

                if (i < marks) seg = ACT;
                else if (i === marks) seg = c.textPri;

                return <div key={String(i)} style={{ flex: 1, height: 4, borderRadius: 2, background: seg }} />;
            })}
        </Row>
    );

    const action = started ? (
        <Col gap={13}>
            <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em', color: ACT }}>{t('app.day.today')}</Meta>
            {segments}
            <div style={{ fontSize: 17, color: c.textPri, letterSpacing: '-0.015em' }}>
                {t('app.task.dayOf', { day: dayNo, days })} ·{' '}
                {t(loggedToday ? 'app.task.logged' : 'app.task.notLogged')}
            </div>
            <Muted style={{ fontSize: 13 }}>
                {t('app.task.shape', {
                    steps: tn('app.task.stepsLower', ex.flow.length),
                    minutes: ex.min ? `, ${tn('app.task.minutesLower', ex.min)}` : '',
                    fields: tn('app.task.reportFields', reportFields),
                })}
            </Muted>
            <Btn primary ic="arrow" onClick={() => navigate(R.taskDay(ex.code))}>
                {t(loggedToday ? 'app.task.viewDayOf' : 'app.task.continueDayOf', { day: dayNo, days })}
            </Btn>
            <Row style={{ justifyContent: 'space-between' }}>
                <Meta style={{ fontSize: 9, color: c.subtle }}>{t('app.task.dayContents')}</Meta>
                <button
                    type="button"
                    onClick={() => {
                        drop(ex.code);
                        navigate(R.tasks);
                    }}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                    <Meta style={{ fontSize: 9, color: '#f87171', letterSpacing: '0.1em' }}>
                        {t('app.task.remove')}
                    </Meta>
                </button>
            </Row>
        </Col>
    ) : (
        <Col gap={13}>
            <Meta style={{ fontSize: 9.5, letterSpacing: '0.16em', color: ACT }}>{t('app.task.start')}</Meta>
            <div style={{ fontSize: 17, color: c.textPri, letterSpacing: '-0.015em' }}>
                {t('app.task.cycle', { days, min: ex.min ?? 5 })}
            </div>
            <Muted style={{ fontSize: 13 }}>{t('app.task.cycle.note')}</Muted>
            {setup}
            <Btn primary ic="arrow" onClick={onStart}>
                {t('app.task.begin')}
            </Btn>
            <Row style={{ justifyContent: 'center' }}>
                <Meta style={{ fontSize: 9, color: c.subtle }}>{t('app.task.endsOn', { date: endLabel })}</Meta>
            </Row>
        </Col>
    );

    const navHeader = (
        <Row style={{ padding: '14px 16px 12px', gap: 10, borderBottom: hair }}>
            <button
                type="button"
                aria-label={t('app.action.back')}
                onClick={back}
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    border: hair,
                    background: c.card,
                    display: 'grid',
                    placeItems: 'center',
                    flex: '0 0 auto',
                    cursor: 'pointer',
                }}
            >
                <Icon k="back" size={15} color={c.textPri} />
            </button>
            <Col style={{ flex: 1, alignItems: 'center' }} gap={3}>
                <Meta style={{ color: c.textPri, letterSpacing: '0.18em' }}>{t('app.tab.tasks')}</Meta>
                <Meta style={{ fontSize: 10, color: started ? accent : c.muted, letterSpacing: '0.12em' }}>
                    {started ? t('app.task.inProgressDay', { day: marks, days }) : t('app.task.notStarted')}
                </Meta>
            </Col>
            <div style={{ width: 34, flex: '0 0 auto' }} />
        </Row>
    );

    const shellProps = {
        active: 'tasks' as const,
        title: ex.name,
        subtitle: started
            ? t('app.task.subtitleRunning', { day: dayNo, days })
            : t('app.task.subtitleNotStarted'),
        index: profile.index,
        email: userProfile?.email || user?.email,
        tasksDone: list.filter((t) => isDoneToday(t)).length,
        tasksTotal: list.length,
        mobileHeader: !desktop ? navHeader : undefined,
        chips: (
            <Link to={R.tasks} style={{ textDecoration: 'none' }}>
                <Meta style={{ fontSize: 10, border: hair, borderRadius: 99, padding: '6px 10px' }}>
                    {t('app.task.allBack')}
                </Meta>
            </Link>
        ),
    };

    if (desktop) {
        return (
            <AppShell {...shellProps}>
                <Appear>
                    <DeskRow>
                        <Fluid>
                            <Panel pad="22px 24px 24px">
                                {header}
                                {metrics}
                            </Panel>
                            {/* Side by side: the arc of the task and what it buys —
                                two answers to "is this worth doing?", read together.
                                `stretch` keeps both cards the same height however
                                uneven the two lists are. */}
                            <Row style={{ gap: 14, alignItems: 'stretch' }}>
                                {flow && (
                                    <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                                        <Panel pad="18px 22px 22px" style={{ flex: 1 }}>
                                            {flow}
                                        </Panel>
                                    </div>
                                )}
                                {changes && (
                                    <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                                        <Panel pad="18px 22px 22px" style={{ flex: 1 }}>
                                            {changes}
                                        </Panel>
                                    </div>
                                )}
                            </Row>
                            {historyBlock && <Panel pad="18px 24px 18px">{historyBlock}</Panel>}
                        </Fluid>
                        {/* Once the task runs, the rail holds only today: the note
                            was the pre-start question and has done its job. */}
                        <Rail width={340}>
                            <Panel pad="20px 20px 20px">{action}</Panel>
                        </Rail>
                    </DeskRow>
                </Appear>
            </AppShell>
        );
    }

    return (
        <AppShell {...shellProps}>
            <Appear>
                <Col style={{ padding: '22px 20px 30px' }} gap={22}>
                    {header}
                    {metrics}
                    {action}
                    {flow}
                    {changes}
                    {started && historyBlock}
                </Col>
            </Appear>
        </AppShell>
    );
};

export default TaskPage;
